import { useEffect, useCallback } from "react";
import {
  getInitialSearchState,
  getInitialFormValues,
} from "../components/priceCheck-form/PriceCheckFormUtils";

export const usePriceCheckAutoSearch = ({
  properties,
  initialEdit,
  hasAutoSearched,
  setHasAutoSearched,
  autoSearchInitiatedRef,
  handleSearchRef,
  setFormValues,
}) => {
  // Helper function to check if saved form values have required data for auto-search
  const hasRequiredSearchData = useCallback((formValues) => {
    return (
      ((formValues.selectedProperties &&
        formValues.selectedProperties.length > 0) ||
        (formValues.selectedCity && formValues.selectedCity.length > 0)) &&
      formValues.dateRanges &&
      formValues.dateRanges.length > 0
    );
  }, []);

  // Helper function to check if auto-search should be triggered
  const shouldTriggerAutoSearch = useCallback((searchState) => {
    return searchState.hasSearchResults && !searchState.isEdit;
  }, []);

  // Helper function to perform auto-search if conditions are met
  const performAutoSearchIfNeeded = useCallback(
    (savedFormValues) => {
      if (hasAutoSearched || autoSearchInitiatedRef.current) return;

      const initialSearchState = getInitialSearchState(initialEdit);

      if (
        shouldTriggerAutoSearch(initialSearchState) &&
        hasRequiredSearchData(savedFormValues)
      ) {
        // Mark that we've initiated auto-search to prevent duplicate calls
        autoSearchInitiatedRef.current = true;
        setHasAutoSearched(true);
        // Trigger search with persisted values (mark as auto-search to preserve filters)
        handleSearchRef.current?.(savedFormValues, null, null, null, true);
      }
    },
    [
      hasAutoSearched,
      initialEdit,
      shouldTriggerAutoSearch,
      hasRequiredSearchData,
      autoSearchInitiatedRef,
      setHasAutoSearched,
      handleSearchRef,
    ]
  );

  // Update form values and auto-trigger search when properties become available
  useEffect(() => {
    // Only proceed if properties are loaded
    if (!properties || properties.length === 0) return;

    const savedFormValues = getInitialFormValues(properties);
    if (savedFormValues && Object.keys(savedFormValues).length > 0) {
      setFormValues(savedFormValues);
      performAutoSearchIfNeeded(savedFormValues);
    }
  }, [properties, performAutoSearchIfNeeded, setFormValues]);

  return {
    hasRequiredSearchData,
    shouldTriggerAutoSearch,
    performAutoSearchIfNeeded,
  };
};
